import { Component, OnInit, ViewChild } from '@angular/core';
import { IdentityService } from '../../../../core/authentication/services/identity.service';
import { UserData } from '../../models/user-data';
import { select, Store } from '@ngrx/store';
import { ChangePasswordDto } from '../../models/change-password-dto';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StateService } from '@uirouter/core';
import { AuthenticationState } from 'account-hybrid/features/authentication/store/authentication/authentication.state';
import {
    getChangePasswordError,
    getChangeUserError,
    getUploadProfilePictureError,
    getUserData,
    isChangePasswordLoading,
    isChangeUserLoading,
    isUploadProfilePictureLoading,
} from 'account-hybrid/features/authentication/store/authentication.selectors';
import {
    changePassword,
    changeUser,
    uploadProfilePicture,
} from '../../store/authentication/authentication.actions';
import {
    selectTwoFactorAuthRecoveryCodes,
    selectTwoFactorAuthSetupData,
    selectTwoFactorAuthSetupError,
    selectTwoFactorAuthSetupIsLoading,
} from 'account-hybrid/features/authentication/store/two-factor-auth-setup.selectors';
import { TwoFactorAuthSetupResponse } from 'account-hybrid/features/authentication/models/two-factor-auth-setup-response';
import {
    resetTwoFactorAuth,
    resetTwoFactorAuthRecoveryCodes,
    setupTwoFactorAuth,
    verifyTwoFactorAuth,
} from 'account-hybrid/features/authentication/store/two-factor-auth-setup/two-factor-auth-setup.actions';
import { CustomEditorComponent } from 'account-shared';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TwoFactorWizardStep } from 'account-hybrid/features/authentication/pages/two-factor-auth-wizard/two-factor-auth-wizard.component';
import { currentPasswordRefusal } from 'account-hybrid/features/authentication/store/two-factor-auth-setup/two-factor-auth-error';

// The profile calls fail with the server's code (INVALID_ARGUMENT, ...), not words, and an edit panel shows an
// error's message: without one, the panel shows its own text.
const withoutServerCode = (error: any) => error && {};

@UntilDestroy()
@Component({
    selector: 'ft-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false
})
export class ProfileComponent implements OnInit {
    @ViewChild('twoFactorAuthStatusEditor')
    twoFactorAuthStatusEditor: CustomEditorComponent;
    userData$: Observable<UserData> = this.store.pipe(select(getUserData));

    changePasswordLoading$: Observable<boolean> = this.store.pipe(
        select(isChangePasswordLoading)
    );
    changeUserLoading$: Observable<boolean> = this.store.pipe(
        select(isChangeUserLoading)
    );
    uploadProfilePictureLoading$: Observable<boolean> = this.store.pipe(
        select(isUploadProfilePictureLoading)
    );

    // A refused current password is the one code the panel names; the others it shows as its own text
    changePasswordError$: Observable<any> = this.store.pipe(
        select(getChangePasswordError),
        map((error) => currentPasswordRefusal(error?.message) ? { message: currentPasswordRefusal(error.message) } : withoutServerCode(error))
    );
    changeUserError$: Observable<any> = this.store.pipe(
        select(getChangeUserError),
        map(withoutServerCode)
    );
    uploadProfilePictureError$: Observable<any> = this.store.pipe(
        select(getUploadProfilePictureError),
        map(withoutServerCode)
    );
    twoFactorSetupIsLoading$: Observable<boolean> = this.store.pipe(
        select(selectTwoFactorAuthSetupIsLoading)
    );
    twoFactorSetupData$: Observable<TwoFactorAuthSetupResponse> =
        this.store.pipe(select(selectTwoFactorAuthSetupData));
    twoFactorAuthRecoveryCodes$: Observable<string[]> = this.store.pipe(
        select(selectTwoFactorAuthRecoveryCodes)
    );
    twoFactorAuthError$: Observable<any> = this.store.pipe(
        select(selectTwoFactorAuthSetupError)
    );
    files: File[];

    twoFactorAuthWizardSteps = TwoFactorWizardStep;

    closeButtonVisible = false;

    constructor(
        public identityService: IdentityService,
        private stateService: StateService,
        private store: Store<AuthenticationState>
    ) {
        this.closeButtonVisible = !!this.getBackUrl();
    }

    ngOnInit() {}

    goBack() {
        document.location.href = this.getBackUrl();
    }

    changeUser(profileData: Partial<UserData>) {
        this.store.dispatch(changeUser({ data: profileData }));
    }

    changePassword(data: ChangePasswordDto) {
        this.store.dispatch(changePassword({ data }));
    }

    uploadProfilePicture(files: File[]) {
        this.store.dispatch(uploadProfilePicture({ file: files[0] }));
    }

    getBackUrl() {
        const params = new URLSearchParams(this.stateService.params);
        return params.get('backUrl');
    }

    turnOffTwoFactorAuth(currentPassword?: string) {
        this.store.dispatch(
            setupTwoFactorAuth({
                is2FAEnabled: false,
                currentPassword,
            })
        );
    }

    turnOnTwoFactorAuth(currentPassword: string) {
        this.store.dispatch(
            setupTwoFactorAuth({
                is2FAEnabled: true,
                currentPassword,
            })
        );
    }

    resetTwoFactorAuthRecoveryCodes(currentPassword?: string) {
        this.store.dispatch(resetTwoFactorAuthRecoveryCodes({ currentPassword }));
    }

    onVerifyTwoFactorAuthOtp({ otp, currentPassword }: { otp: string; currentPassword?: string }) {
        this.store.dispatch(verifyTwoFactorAuth({ otp, currentPassword }));
    }

    onTwoFactorAuthWizardOpened(step: TwoFactorWizardStep) {
        if (step === TwoFactorWizardStep.Setup) {
            this.store.dispatch(
                setupTwoFactorAuth({
                    is2FAEnabled: true,
                })
            );
        }
    }

    onResetTwoFactorAuth(currentPassword?: string) {
        this.store.dispatch(resetTwoFactorAuth({ currentPassword }));
    }
}
